import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Delete as DeleteIcon,
  Event as RescheduleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import AUTH_CONTEXT from "../../../store";
import { PhysioServices } from "../../../services/physio_services";
import classes from "./PhysioSlots.module.css";

const PhysioSlots = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = AUTH_CONTEXT();
  const doctorName = searchParams.get("name") || "";

  // Check for reschedule mode
  const isRescheduleMode = searchParams.get("reschedule") === "true";
  const videoSessionId = searchParams.get("videoSessionId");
  const sessionType = searchParams.get("sessionType");
  const isDoctorChanged = searchParams.get("isDoctorChanged") === "true";
  const newDoctorId = searchParams.get("newDoctorId");

  const [isGenerating, setIsGenerating] = useState(false);
  const [slotsData, setSlotsData] = useState({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  // Week-based cache: { "2024-01-01": { data: {}, timestamp: 123456789 } }
  const [weekCache, setWeekCache] = useState(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Helper function to check if slot should be shown in reschedule mode
  const shouldShowSlotInRescheduleMode = (slot) => {
    if (!rescheduleMode || slot.status !== "available") {
      return true; // Show all slots when not in reschedule mode or if slot is not available
    }

    // In reschedule mode, filter by session type
    if (sessionType === "paid") {
      return slot.type === "paid"; // Only show paid slots for paid session reschedule
    } else if (sessionType === "free") {
      return slot.type === "free"; // Only show free slots for free session reschedule
    }

    return true; // Default: show all slots
  };

  // Helper function to check if slot should be shown based on type filter
  const shouldShowSlotByTypeFilter = (slot) => {
    if (slotTypeFilter === "all") {
      return true;
    }
    return slot.type === slotTypeFilter;
  };

  // Combined filter function
  const shouldShowSlot = (slot) => {
    return shouldShowSlotInRescheduleMode(slot) && shouldShowSlotByTypeFilter(slot);
  };

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get today's date in YYYY-MM-DD format for date input min attribute
  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Get today's date as default
  const getTodayDateRange = () => {
    const today = new Date();
    return formatDateForDisplay(today);
  };

  // State for configuration
  const [dateRange, setDateRange] = useState(getTodayDateRange());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [endDate, setEndDate] = useState(getTodayDateString());
  const [selectedDays, setSelectedDays] = useState([
    false,
    true,
    true,
    true,
    true,
    true,
    false,
  ]); // Mon-Fri selected (Sunday first)
  const [timeRanges, setTimeRanges] = useState([
    {
      id: 1,
      startTime: "",
      endTime: "",
      type: "free", // free or paid
    },
  ]);
  const [currentWeek, setCurrentWeek] = useState("Nov 1 - Nov 7, 2023");
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [calendarError, setCalendarError] = useState("");
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  // Reschedule mode states
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSlotForDelete, setSelectedSlotForDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState("single");
  const [deleteRangeStart, setDeleteRangeStart] = useState("");
  const [deleteRangeEnd, setDeleteRangeEnd] = useState("");

  // Filter states
  const [slotTypeFilter, setSlotTypeFilter] = useState("all"); // "all", "free", "paid"

  // Check if there are any valid time ranges
  const hasValidTimeRanges = React.useMemo(() => {
    return timeRanges.some((range) => range.startTime && range.endTime);
  }, [timeRanges]);

  // Generate cache key for a week
  const generateWeekCacheKey = (weekStart) => {
    return formatDateForAPI(weekStart);
  };

  // Check if cached data is still valid
  const isCacheValid = (cacheEntry) => {
    if (!cacheEntry) return false;
    const now = Date.now();
    return now - cacheEntry.timestamp < CACHE_DURATION;
  };

  // Get cached data for a week
  const getCachedWeekData = (weekStart) => {
    const cacheKey = generateWeekCacheKey(weekStart);
    const cacheEntry = weekCache.get(cacheKey);

    if (cacheEntry && isCacheValid(cacheEntry)) {
      return cacheEntry.data;
    }
    return null;
  };

  // Cache week data
  const cacheWeekData = (weekStart, data) => {
    const cacheKey = generateWeekCacheKey(weekStart);
    const newCache = new Map(weekCache);
    newCache.set(cacheKey, {
      data: data,
      timestamp: Date.now(),
    });

    // Limit cache size to prevent memory issues (keep last 10 weeks)
    if (newCache.size > 10) {
      const firstKey = newCache.keys().next().value;
      newCache.delete(firstKey);
    }

    setWeekCache(newCache);
  };

  // Clear cache (useful after slot generation/deletion)
  const clearCache = () => {
    setWeekCache(new Map());
  };

  // Fetch slots for a given week
  // updateDisplay: when true, set slotsData (for the currently viewed week); when false, only update cache (used when refreshing other weeks after generate)
  const fetchSlotsForWeek = async (
    weekStart,
    forceRefresh = false,
    updateDisplay = true
  ) => {
    if (!userId || !token) {
      return;
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = getCachedWeekData(weekStart);
      if (cachedData) {
        console.log(
          "📦 Using cached data for week:",
          formatDateForAPI(weekStart)
        );
        if (updateDisplay) {
          setSlotsData(cachedData);
        }
        return;
      }
    }

    console.log(
      "🌐 Fetching fresh data for week:",
      formatDateForAPI(weekStart)
    );
    if (updateDisplay) {
      setIsLoadingSlots(true);
    }
    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startDateStr = formatDateForAPI(weekStart);
      const endDateStr = formatDateForAPI(weekEnd);

      const timeOffSet = getTimezoneOffset();

      const response = await PhysioServices.getDoctorSlotDetails(
        token,
        navigate,
        {
          DoctorUserID: userId,
          StartDate: startDateStr,
          EndDate: endDateStr,
        },
        timeOffSet
      );

      if (response.status === 200 && response.data) {
        const transformed = transformSlotsData(response.data, weekStart);
        if (updateDisplay) {
          setSlotsData(transformed);
        }
        // Always cache the successful response so other weeks stay fresh when user navigates
        cacheWeekData(weekStart, transformed);
      } else {
        if (updateDisplay) {
          setSlotsData({});
          const errorMessage =
            response?.error?.trim() || "Something went wrong";
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      if (updateDisplay) {
        setSlotsData({});
        toast.error("Something went wrong");
      }
    } finally {
      if (updateDisplay) {
        setIsLoadingSlots(false);
      }
    }
  };

  // Format date to YYYY-MM-DD
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Transform API response to UI format
  const transformSlotsData = (apiData, weekStart) => {
    if (!apiData || !apiData.DateSlots) {
      return {};
    }

    const transformed = {};
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Get week dates
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      weekDates.push(date);
    }

    weekDates.forEach((date) => {
      const dayName = dayNames[date.getDay()];
      const dateNum = String(date.getDate()).padStart(2, "0");
      // Convert date to DD-MM-YYYY format to match API response
      const apiDateKey = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${date.getFullYear()}`;

      const slots = apiData.DateSlots[apiDateKey] || [];

      transformed[`${dayName} ${dateNum}`] = slots.map((slot) => ({
        slotId: slot.SlotID,
        time: slot.Timing,
        slotTime: slot.SlotTime, // UTC time for operations
        type: slot.IsFreeSlot ? "free" : "paid",
        status: slot.IsBooked ? "booked" : "available",
        title: slot.IsBooked ? "Booked" : "Available",
        duration: slot.Duration,
        isActive: slot.IsActive,
      }));
    });

    return transformed;
  };

  // Get timezone offset from browser (in minutes, negative for ahead of UTC)
  // API expects positive offset, so we negate it
  const getTimezoneOffset = () => {
    return -new Date().getTimezoneOffset();
  };

  // Initialize current week to today's week and select today's day in Repeat on Days
  useEffect(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    const day = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    startOfWeek.setDate(today.getDate() - day); // Go to Sunday of current week

    setCurrentWeekStart(startOfWeek);
    updateCurrentWeekDisplay(startOfWeek);

    // Select today's day in Repeat on Days
    const initialSelectedDays = [
      false,
      false,
      false,
      false,
      false,
      false,
      false,
    ];
    initialSelectedDays[day] = true;
    setSelectedDays(initialSelectedDays);

    if (userId && token) {
      fetchSlotsForWeek(startOfWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  // Initialize reschedule mode if coming from video sessions
  useEffect(() => {
    if (isRescheduleMode && videoSessionId && sessionType) {
      console.log("Initializing reschedule mode:", {
        videoSessionId,
        sessionType,
      });
      setRescheduleMode(true);

      // Show info toast about reschedule mode
      toast.info(
        `Reschedule mode: Select a new slot for the ${sessionType} session`,
        {
          position: "bottom-left",
          autoClose: 5000,
        }
      );
    }
  }, [isRescheduleMode, videoSessionId, sessionType]);

  // Debug modal state
  useEffect(() => {
    console.log("Date picker modal state changed:", showDatePicker);

    // Add/remove body class to prevent scrolling when modal is open
    if (showDatePicker) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.classList.remove("modal-open");
    };
  }, [showDatePicker]);

  const updateCurrentWeekDisplay = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const formatDate = (date) => {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    const year = weekStart.getFullYear();
    setCurrentWeek(
      `${formatDate(weekStart)} - ${formatDate(weekEnd)}, ${year}`
    );
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + direction * 7);

    setCurrentWeekStart(newWeekStart);
    updateCurrentWeekDisplay(newWeekStart);
    // Fetch slots for the new week
    fetchSlotsForWeek(newWeekStart);
  };

  const handleWeekPickerClick = () => {
    // Set calendar month to the month of current week when opening
    setCalendarMonth(new Date(currentWeekStart));
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setCalendarError("");
    setShowWeekPicker(true);
  };

  const navigateCalendarMonth = (direction) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(calendarMonth.getMonth() + direction);
    setCalendarMonth(newMonth);
  };

  const handleDateSelect = (selectedDate) => {
    const date = new Date(selectedDate);
    setCalendarError("");

    if (!selectedStartDate) {
      // First click - set start date
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else if (!selectedEndDate) {
      // Second click - set end date and validate
      const startTime = selectedStartDate.getTime();
      const endTime = date.getTime();
      const daysDifference = Math.abs(
        (endTime - startTime) / (1000 * 60 * 60 * 24)
      );

      if (daysDifference !== 6) {
        // Not exactly 7 days (6 days difference)
        setCalendarError(
          "Please select dates that are exactly 7 days apart (one week)"
        );
        setSelectedEndDate(null);
        return;
      }

      // Determine which date is earlier
      const earlierDate = startTime < endTime ? selectedStartDate : date;

      // Set the week to start from the earlier date
      const newWeekStart = new Date(earlierDate);
      setCurrentWeekStart(newWeekStart);
      updateCurrentWeekDisplay(newWeekStart);
      setShowWeekPicker(false);

      // Fetch slots for the new week
      fetchSlotsForWeek(newWeekStart);

      // Reset selection
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  };

  const clearSelection = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setCalendarError("");
  };

  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + 1,
      0
    );

    // Start from the Sunday before the first day of the month
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    // End on the Saturday after the last day of the month
    const endDate = new Date(lastDayOfMonth);
    endDate.setDate(lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay()));

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const isDateInCurrentWeek = (date) => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);
    return date >= currentWeekStart && date <= weekEnd;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate) return false;

    if (selectedStartDate && !selectedEndDate) {
      return date.toDateString() === selectedStartDate.toDateString();
    }

    if (selectedStartDate && selectedEndDate) {
      const startTime = Math.min(
        selectedStartDate.getTime(),
        selectedEndDate.getTime()
      );
      const endTime = Math.max(
        selectedStartDate.getTime(),
        selectedEndDate.getTime()
      );
      const dateTime = date.getTime();
      return dateTime >= startTime && dateTime <= endTime;
    }

    return false;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;

    const startTime = Math.min(
      selectedStartDate.getTime(),
      selectedEndDate.getTime()
    );
    const endTime = Math.max(
      selectedStartDate.getTime(),
      selectedEndDate.getTime()
    );
    const dateTime = date.getTime();

    return dateTime > startTime && dateTime < endTime;
  };

  // Use only API response data for slots
  const weekSlots = slotsData;

  const dayLabels = ["S", "M", "T", "W", "T", "F", "S"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Generate week header data from currentWeekStart
  const getWeekHeaderData = () => {
    const headers = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      const dayName = dayNames[date.getDay()];
      const dateNum = String(date.getDate()).padStart(2, "0");
      const today = new Date();
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      headers.push({
        key: `${dayName} ${dateNum}`,
        dayName,
        date: dateNum,
        isToday,
      });
    }
    return headers;
  };

  const handleDateRangeClick = () => {
    // Initialize the modal date inputs with current values if they exist
    if (!startDate || !endDate) {
      const todayStr = getTodayDateString();
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else {
      // Validate existing dates are not in the past
      const today = new Date();
      const selectedStartDate = new Date(startDate);

      today.setHours(0, 0, 0, 0);
      selectedStartDate.setHours(0, 0, 0, 0);

      if (selectedStartDate < today) {
        // Reset to today if current start date is in the past
        const todayStr = getTodayDateString();
        setStartDate(todayStr);
        setEndDate(todayStr);
      }
    }

    console.log("Opening date picker modal with portal");
    setShowDatePicker(true);
  };

  const handleQuickDateRange = (days) => {
    const today = new Date();
    const endDateObj = new Date(today);

    if (days === 1) {
      // For "Today", start and end date are the same
      endDateObj.setDate(today.getDate());
    } else {
      // For other ranges, calculate end date
      endDateObj.setDate(today.getDate() + days - 1);
    }

    const formatDate = (date) => {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatDateForInput = (date) => {
      return date.toISOString().split("T")[0]; // YYYY-MM-DD format for input fields
    };

    const newRange =
      days === 1
        ? formatDate(today) // Just show today's date
        : `${formatDate(today)} - ${formatDate(endDateObj)}`;

    // Update the display text
    setDateRange(newRange);

    // Update the modal input fields - ensure we don't set past dates
    const startDateStr = formatDateForInput(today);
    const endDateStr = formatDateForInput(endDateObj);
    setStartDate(startDateStr);
    setEndDate(endDateStr);

    // Auto-select day if start and end dates are the same
    if (startDateStr === endDateStr) {
      autoSelectDayFromDate(startDateStr);
    }
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const formatDate = (date) => {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      };

      if (startDate === endDate) {
        setDateRange(formatDate(start));
      } else {
        setDateRange(`${formatDate(start)} - ${formatDate(end)}`);
      }
    }
  };

  const handleApplyCustomRange = () => {
    // Validate that start date is not in the past
    const today = new Date();
    const selectedStartDate = new Date(startDate);
    const selectedEndDate = new Date(endDate);

    // Set time to start of day for comparison
    today.setHours(0, 0, 0, 0);
    selectedStartDate.setHours(0, 0, 0, 0);
    selectedEndDate.setHours(0, 0, 0, 0);

    if (selectedStartDate < today) {
      toast.error("Start date cannot be in the past");
      return;
    }

    if (selectedEndDate < selectedStartDate) {
      toast.error("End date cannot be before start date");
      return;
    }

    handleCustomDateChange();

    // Auto-select days in REPEAT ON DAYS based on date range
    const daysDifference = Math.ceil((selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    
    if (daysDifference <= 7) {
      console.log(`Date range is ${daysDifference} days, auto-selecting specific days in REPEAT ON DAYS`);
      autoSelectDaysFromDateRange(selectedStartDate, selectedEndDate);
    } else {
      console.log(`Date range is ${daysDifference} days (more than 7), selecting all days in REPEAT ON DAYS`);
      // For ranges longer than 7 days, select all days
      setSelectedDays([true, true, true, true, true, true, true]);
    }

    setShowDatePicker(false);
  };

  // Auto-select day of week when start and end dates are the same
  const autoSelectDayFromDate = (dateStr) => {
    if (!dateStr) return;

    const date = new Date(dateStr);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    const newSelectedDays = [false, false, false, false, false, false, false];
    newSelectedDays[dayOfWeek] = true;
    setSelectedDays(newSelectedDays);
  };

  // Auto-select days from date range (for ranges of 7 days or less)
  const autoSelectDaysFromDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return;

    const newSelectedDays = [false, false, false, false, false, false, false];
    const currentDate = new Date(startDate);
    const endDateObj = new Date(endDate);

    // Iterate through each date in the range
    while (currentDate <= endDateObj) {
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      newSelectedDays[dayOfWeek] = true;
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setSelectedDays(newSelectedDays);
    console.log('Auto-selected days based on date range:', newSelectedDays);
  };

  const handleDayToggle = (index) => {
    const newSelectedDays = [...selectedDays];
    newSelectedDays[index] = !newSelectedDays[index];
    setSelectedDays(newSelectedDays);
  };

  const handleSelectAll = () => {
    const allSelected = selectedDays.every((day) => day);
    if (allSelected) {
      // If all are selected, deselect all
      setSelectedDays([false, false, false, false, false, false, false]);
    } else {
      // If not all are selected, select all
      setSelectedDays([true, true, true, true, true, true, true]);
    }
  };

  const addTimeRange = () => {
    const newRange = {
      id: timeRanges.length + 1,
      startTime: "",
      endTime: "",
      type: "free",
    };
    setTimeRanges([...timeRanges, newRange]);
  };

  const removeTimeRange = (id) => {
    setTimeRanges(timeRanges.filter((range) => range.id !== id));
  };

  const updateTimeRange = (id, field, value) => {
    setTimeRanges(
      timeRanges.map((range) =>
        range.id === id ? { ...range, [field]: value } : range
      )
    );

    // The useEffect will handle validation automatically
  };

  // Convert local time (HH:MM) to UTC time (HH:MM)
  const convertLocalTimeToUTC = (localTime) => {
    const [hours, minutes] = localTime.split(":").map(Number);
    const today = new Date();

    // Create a date object with local time (this uses local timezone)
    const localDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes
    );

    // Get UTC hours and minutes directly
    // JavaScript Date automatically handles timezone conversion
    const utcHours = localDate.getUTCHours();
    const utcMinutes = localDate.getUTCMinutes();

    return `${String(utcHours).padStart(2, "0")}:${String(utcMinutes).padStart(
      2,
      "0"
    )}`;
  };

  // Generate time slots from a time range
  const generateTimeSlots = (startTime, endTime, duration) => {
    const slots = [];
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Validate time range
    if (startMinutes >= endMinutes) {
      return slots; // Invalid range, return empty
    }

    let currentMinutes = startMinutes;

    if (duration === 900) {
      // FREE slots: 15-minute slots with 30-minute intervals
      // Example: 9:00-10:00 generates slots at 9:00, 9:30 (each 15 min duration)
      while (currentMinutes + 15 <= endMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const minutes = currentMinutes % 60;
        const timeString = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;

        // Convert to UTC
        const utcTime = convertLocalTimeToUTC(timeString);
        slots.push(utcTime);

        currentMinutes += 30; // Move to next slot position (30-minute interval)
      }
    } else {
      // PAID slots: 30-minute slots with 30-minute intervals
      while (currentMinutes + 30 <= endMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const minutes = currentMinutes % 60;
        const timeString = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;

        // Convert to UTC
        const utcTime = convertLocalTimeToUTC(timeString);
        slots.push(utcTime);

        currentMinutes += 30; // Move to next slot position
      }
    }

    return slots;
  };

  // Map selectedDays array to DaysOfWeek (0=Sunday, 1=Monday, etc.)
  const getDaysOfWeek = () => {
    const days = [];
    selectedDays.forEach((isSelected, index) => {
      if (isSelected) {
        days.push(index); // index 0 = Sunday, 1 = Monday, etc.
      }
    });
    return days;
  };

  // Calculate maxDaysAllowed from date range
  const calculateMaxDays = (startDateStr, endDateStr) => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  // Build API request body
  const buildSlotRequest = () => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range");
      return null;
    }

    // Validate date range (max 2 months)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const maxDate = new Date(start);
    maxDate.setMonth(maxDate.getMonth() + 2);

    if (end > maxDate) {
      toast.error("Date range cannot exceed 2 months");
      return null;
    }

    if (start > end) {
      toast.error("Start date cannot be after end date");
      return null;
    }

    if (!userId) {
      toast.error("Doctor ID is missing");
      return null;
    }

    if (timeRanges.length === 0) {
      toast.error("Please add at least one time range");
      return null;
    }

    const daysOfWeek = getDaysOfWeek();
    if (daysOfWeek.length === 0) {
      toast.error("Please select at least one day of the week");
      return null;
    }

    // Collect all time slots grouped by type
    const freeTimeSlots = [];
    const paidTimeSlots = [];

    timeRanges.forEach((range) => {
      // Validate time range
      if (!range.startTime || !range.endTime) {
        return; // Skip invalid ranges
      }

      const duration = range.type === "free" ? 900 : 1800;
      const slots = generateTimeSlots(range.startTime, range.endTime, duration);

      if (range.type === "free") {
        freeTimeSlots.push(...slots);
      } else {
        paidTimeSlots.push(...slots);
      }
    });

    // Remove duplicates and sort
    const uniqueFreeSlots = [...new Set(freeTimeSlots)].sort();
    const uniquePaidSlots = [...new Set(paidTimeSlots)].sort();

    if (uniqueFreeSlots.length === 0 && uniquePaidSlots.length === 0) {
      toast.error(
        "No valid time slots generated. Please check your time ranges."
      );
      return null;
    }

    const maxDays = calculateMaxDays(startDate, endDate);

    // Build requests for free and paid slots separately
    const requests = [];

    if (uniqueFreeSlots.length > 0) {
      requests.push({
        DoctorUserID: userId,
        Duration: 900,
        DaysOfWeek: daysOfWeek,
        TimeSlots: uniqueFreeSlots,
        StartDate: startDate,
        EndDate: endDate,
        maxDaysAllowed: maxDays,
        IsFreeSlot: true,
      });
    }

    if (uniquePaidSlots.length > 0) {
      requests.push({
        DoctorUserID: userId,
        Duration: 1800,
        DaysOfWeek: daysOfWeek,
        TimeSlots: uniquePaidSlots,
        StartDate: startDate,
        EndDate: endDate,
        maxDaysAllowed: maxDays,
        IsFreeSlot: false,
      });
    }

    return requests;
  };

  // Handle Generate Slots button click
  const handleGenerateSlots = async () => {
    // Check if there are valid time ranges
    if (!hasValidTimeRanges) {
      toast.error(
        "Please add at least one time range with start and end times."
      );
      return;
    }

    const requests = buildSlotRequest();

    if (!requests || requests.length === 0) {
      return;
    }

    setIsGenerating(true);
    const timeOffSet = getTimezoneOffset();

    try {
      // Make API calls for each request (free and paid separately)
      const promises = requests.map((request) =>
        PhysioServices.addDoctorSlotForVideoSession(
          token,
          navigate,
          request,
          timeOffSet
        )
      );

      const results = await Promise.all(promises);

      // Check if all requests succeeded
      const allSuccess = results.every((result) => result.status === 200);

      if (allSuccess) {
        toast.success("Slots generated successfully!");
        // Clear cache so we never serve stale data after generate
        clearCache();
        // Clear displayed slots so we don't show old data; refetch will repopulate
        setSlotsData({});
        setIsLoadingSlots(true);

        // Await refetch for the currently viewed week so UI shows fresh data (avoids race with cache)
        await fetchSlotsForWeek(currentWeekStart, true, true);

        // Refresh cache for other affected weeks (don't update display — only current week is shown)
        const generatedStartDate = new Date(startDate);
        const generatedEndDate = new Date(endDate);
        const generatedWeekStart = new Date(generatedStartDate);
        generatedWeekStart.setDate(
          generatedStartDate.getDate() - generatedStartDate.getDay()
        );

        if (generatedWeekStart.getTime() !== currentWeekStart.getTime()) {
          await fetchSlotsForWeek(generatedWeekStart, true, false);
          const generatedWeekEnd = new Date(generatedWeekStart);
          generatedWeekEnd.setDate(generatedWeekStart.getDate() + 6);
          if (generatedEndDate > generatedWeekEnd) {
            const nextWeekStart = new Date(generatedWeekStart);
            nextWeekStart.setDate(generatedWeekStart.getDate() + 7);
            await fetchSlotsForWeek(nextWeekStart, true, false);
          }
        }
      } else {
        const failedResult = results.find((r) => r.status !== 200);
        const errorMessage =
          failedResult?.error?.trim() || "Something went wrong";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error generating slots:", error);
      toast.error("Something went wrong");
    } finally {
      setIsGenerating(false);
    }
  };

  // Toggle bulk delete mode
  const handleToggleBulkDelete = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedSlots(new Set()); // Clear selections when toggling mode
  };

  // Handle slot selection for bulk operations
  const handleSlotSelect = (slotId, isSelected) => {
    const newSelectedSlots = new Set(selectedSlots);
    if (isSelected) {
      newSelectedSlots.add(slotId);
    } else {
      newSelectedSlots.delete(slotId);
    }
    setSelectedSlots(newSelectedSlots);
  };

  // Handle slot selection for reschedule
  const handleRescheduleSlotSelect = (slot) => {
    if (selectedRescheduleSlot === slot.slotId) {
      // Deselect if already selected
      setSelectedRescheduleSlot(null);
    } else {
      // Select new slot (only one can be selected for reschedule)
      setSelectedRescheduleSlot(slot.slotId);
      console.log("Selected slot for reschedule:", slot);
    }
  };

  // Handle reschedule confirmation
  const handleConfirmReschedule = async () => {
    if (!selectedRescheduleSlot || !videoSessionId) {
      toast.error("Please select a slot to reschedule", {
        position: "bottom-left",
        autoClose: 3000,
      });
      return;
    }

    // Find the selected slot details for confirmation
    let selectedSlotDetails = null;
    Object.values(slotsData).forEach((daySlots) => {
      const slot = daySlots.find((s) => s.slotId === selectedRescheduleSlot);
      if (slot) {
        selectedSlotDetails = slot;
      }
    });

    if (!selectedSlotDetails) {
      toast.error("Selected slot details not found", {
        position: "bottom-left",
        autoClose: 3000,
      });
      return;
    }

    // Show confirmation alert with actual slot date/time
    const slotTimeUTC = selectedSlotDetails.slotTime;

    if (!slotTimeUTC) {
      toast.error("Slot time information not found", {
        position: "bottom-left",
        autoClose: 3000,
      });
      return;
    }

    // Parse the UTC SlotTime to get readable date and time for confirmation
    const utcDateTime = new Date(slotTimeUTC);
    const readableDate = utcDateTime.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
    const readableTime = utcDateTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const confirmMessage = `Are you sure you want to reschedule this ${sessionType} session to:\n\nDate: ${readableDate}\nTime: ${readableTime}\nSlot Type: ${selectedSlotDetails.type.toUpperCase()}\n\nThis action cannot be undone.`;

    const isConfirmed = window.confirm(confirmMessage);

    if (!isConfirmed) {
      return; // User cancelled the reschedule
    }

    try {
      setIsRescheduling(true);

      // Use the SlotTime from API response which is already in UTC format
      // SlotTime format: "2026-01-27T00:30:00.000Z"
      const slotTimeUTC = selectedSlotDetails.slotTime;

      if (!slotTimeUTC) {
        toast.error("Slot time information not found", {
          position: "bottom-left",
          autoClose: 3000,
        });
        return;
      }

      // Parse the UTC SlotTime to extract date and time
      const utcDateTime = new Date(slotTimeUTC);

      // Extract date in YYYY-MM-DD format (UTC)
      const utcDate = utcDateTime.toISOString().split("T")[0];

      // Extract time in HH:MM format (UTC)
      const utcTimeStr = utcDateTime
        .toISOString()
        .split("T")[1]
        .substring(0, 5);

      const rescheduleData = {
        VideoSessionID: videoSessionId,
        NewSessionDate: utcDate, // YYYY-MM-DD format in UTC
        NewSessionTime: utcTimeStr, // HH:MM format in UTC, 24-hour format
        IsDoctorChanged: sessionType === "free" ? isDoctorChanged : false,
        NewDoctorUserID:
          sessionType === "free" && isDoctorChanged ? newDoctorId : "",
      };

      console.log("Reschedule API call data:", rescheduleData);
      console.log("Original slot details:", selectedSlotDetails);
      console.log("SlotTime from API:", slotTimeUTC);
      console.log("Parsed UTC date:", utcDate);
      console.log("Parsed UTC time:", utcTimeStr);
      console.log("Session type:", sessionType);
      console.log("Is doctor changed:", isDoctorChanged);
      console.log("New doctor ID:", newDoctorId);

      const result = await PhysioServices.rescheduleVideoSession(
        token,
        navigate,
        rescheduleData
      );

      if (result.status === 200) {
        toast.success("Session rescheduled successfully!", {
          position: "bottom-left",
          autoClose: 3000,
        });

        // Navigate back to video sessions page
        navigate("/physio/video-sessions");
      } else {
        toast.error(
          `Failed to reschedule session: ${result.error || "Unknown error"}`,
          {
            position: "bottom-left",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Reschedule error:", error);
      toast.error(`Error rescheduling session: ${error.message}`, {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  // Handle cancel reschedule
  const handleCancelReschedule = () => {
    setRescheduleMode(false);
    setSelectedRescheduleSlot(null);
    navigate("/physio/video-sessions");
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (slot) => {
    setSelectedSlotForDelete(slot);
    setDeleteOption("single");
    setDeleteRangeStart(getTodayDateString());
    setDeleteRangeEnd(getTodayDateString());
    setShowDeleteModal(true);
  };

  // Handle delete modal proceed
  const handleDeleteModalProceed = async () => {
    if (!selectedSlotForDelete) return;

    if (deleteOption === "single") {
      // Delete single slot
      await handleDeleteSingleSlot(selectedSlotForDelete.slotId);
      setShowDeleteModal(false);
    } else if (deleteOption === "bulk") {
      // Activate bulk delete mode and pre-select the current slot
      setShowDeleteModal(false);
      setBulkDeleteMode(true);
      // Pre-select the slot that was clicked
      setSelectedSlots(new Set([selectedSlotForDelete.slotId]));
    } else if (deleteOption === "range") {
      // Delete same time in date range
      await handleDeleteSameTimeInRange();
      setShowDeleteModal(false);
    }
  };

  // Handle delete same time in range
  const handleDeleteSameTimeInRange = async () => {
    if (!selectedSlotForDelete || !deleteRangeStart || !deleteRangeEnd) {
      toast.error("Please select a valid date range");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete all ${selectedSlotForDelete.time} slots (both FREE and PAID) from ${deleteRangeStart} to ${deleteRangeEnd}?`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      const response = await PhysioServices.deleteDoctorSlots(token, navigate, {
        DoctorUserID: userId,
        StartDate: deleteRangeStart,
        EndDate: deleteRangeEnd,
        SlotIDs: [selectedSlotForDelete.slotId],
        DeleteAllSlotsOfSameTime: true,
      });

      if (response.status === 200 && response.data?.IsSuccess) {
        toast.success(
          `All ${selectedSlotForDelete.time} slots (FREE & PAID) deleted successfully in the selected range!`
        );
        // Clear cache and refresh slots data
        clearCache();
        fetchSlotsForWeek(currentWeekStart, true);
      } else {
        const errorMessage =
          response?.error?.trim() || "Failed to delete slots";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting slots:", error);
      toast.error("Something went wrong while deleting slots");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete single slot
  const handleDeleteSingleSlot = async (slotId) => {
    setIsDeleting(true);

    try {
      const response = await PhysioServices.deleteDoctorSlots(token, navigate, {
        DoctorUserID: userId,
        SlotIDs: [slotId],
        DeleteAllSlotsOfSameTime: false,
      });

      if (response.status === 200 && response.data?.IsSuccess) {
        toast.success("Slot deleted successfully!");
        // Clear cache and refresh slots data
        clearCache();
        fetchSlotsForWeek(currentWeekStart, true);
      } else {
        const errorMessage = response?.error?.trim() || "Failed to delete slot";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting slot:", error);
      toast.error("Something went wrong while deleting slot");
    } finally {
      setIsDeleting(false);
    }
  };
  // Delete selected slots
  const handleDeleteSelectedSlots = async () => {
    if (selectedSlots.size === 0) {
      toast.error("Please select slots to delete");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedSlots.size} selected slot(s)?`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      // Convert Set to Array for API call
      const slotIds = Array.from(selectedSlots);

      const response = await PhysioServices.deleteDoctorSlots(token, navigate, {
        DoctorUserID: userId,
        SlotIDs: slotIds,
        DeleteAllSlotsOfSameTime: false,
      });

      if (response.status === 200 && response.data?.IsSuccess) {
        toast.success(`${selectedSlots.size} slot(s) deleted successfully!`);
        setSelectedSlots(new Set());
        setBulkDeleteMode(false); // Exit bulk delete mode after deletion
        // Clear cache and refresh slots data
        clearCache();
        fetchSlotsForWeek(currentWeekStart, true);
      } else {
        const errorMessage =
          response?.error?.trim() || "Failed to delete slots";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting slots:", error);
      toast.error("Something went wrong while deleting slots");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.mainContent}>
        {/* Sidebar */}
        <aside className={classes.sidebar}>
          <div className={classes.sidebarHeader}>
            <Typography variant="h6" className={classes.sidebarTitle}>
              <CalendarIcon className={classes.sidebarIcon} />
              Configure Availability
            </Typography>
            <Typography variant="caption" className={classes.sidebarSubtitle}>
              Set date range, days, and time slots to generate slots.
            </Typography>
          </div>

          <div className={classes.sidebarContent}>
            {/* Date Range Selection */}
            <div className={classes.configSection}>
              <div className={classes.sectionHeader}>
                <Typography variant="caption" className={classes.sectionLabel}>
                  1. SELECT DATE RANGE
                </Typography>
              </div>

              <div className={classes.dateInputContainer}>
                <CalendarIcon className={classes.dateIcon} />
                <TextField
                  value={dateRange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleDateRangeClick}
                  InputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                  }}
                  className={classes.dateInput}
                  placeholder="Select date range"
                />
                {showDatePicker &&
                  (typeof document !== "undefined" && document.body ? (
                    createPortal(
                      <div
                        className={classes.datePickerOverlay}
                        style={{
                          position: "fixed",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 2147483647,
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => setShowDatePicker(false)}
                      >
                        <div
                          className={classes.datePickerContainer}
                          style={{
                            position: "relative",
                            zIndex: 2147483647,
                            backgroundColor: "white",
                            borderRadius: "16px",
                            padding: "24px",
                            maxWidth: "400px",
                            width: "90%",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onLoad={() =>
                            console.log("Date picker modal rendered via portal")
                          }
                        >
                          <div className={classes.datePickerHeader}>
                            <Typography variant="subtitle2">
                              Select Date Range
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setShowDatePicker(false)}
                              className={classes.closeDatePicker}
                            >
                              ✕
                            </IconButton>
                          </div>
                          <div className={classes.datePickerContent}>
                            <Typography
                              variant="caption"
                              className={classes.datePickerLabel}
                            >
                              Quick Select:
                            </Typography>
                            <div className={classes.quickDateButtons}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleQuickDateRange(1)}
                                className={classes.quickDateButton}
                              >
                                Today
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleQuickDateRange(7)}
                                className={classes.quickDateButton}
                              >
                                Next 7 Days
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleQuickDateRange(14)}
                                className={classes.quickDateButton}
                              >
                                Next 14 Days
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleQuickDateRange(30)}
                                className={classes.quickDateButton}
                              >
                                Next 30 Days
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleQuickDateRange(60)}
                                className={classes.quickDateButton}
                              >
                                Next 60 Days
                              </Button>
                            </div>
                            <div className={classes.customDateInputs}>
                              <Typography
                                variant="caption"
                                className={classes.datePickerLabel}
                              >
                                Or select custom range:
                              </Typography>
                              <div className={classes.dateInputRow}>
                                <TextField
                                  type="date"
                                  size="medium"
                                  label="Start Date"
                                  value={startDate}
                                  onChange={(e) => {
                                    const newStartDate = e.target.value;
                                    setStartDate(newStartDate);
                                    // If end date is before new start date, update end date
                                    if (endDate && newStartDate > endDate) {
                                      setEndDate(newStartDate);
                                    }
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  className={classes.customDateInput}
                                  inputProps={{
                                    min: getTodayDateString(),
                                  }}
                                />
                                <TextField
                                  type="date"
                                  size="medium"
                                  label="End Date"
                                  value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                  className={classes.customDateInput}
                                  inputProps={{
                                    min: startDate || getTodayDateString(),
                                  }}
                                />
                              </div>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                className={classes.applyDateButton}
                                onClick={handleApplyCustomRange}
                                disabled={!startDate || !endDate}
                              >
                                Apply Range
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>,
                      document.body
                    )
                  ) : (
                    // Fallback: render in place with very high z-index
                    <div
                      className={classes.datePickerOverlay}
                      style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2147483647,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => setShowDatePicker(false)}
                    >
                      <div
                        className={classes.datePickerContainer}
                        style={{
                          position: "relative",
                          zIndex: 2147483647,
                          backgroundColor: "white",
                          borderRadius: "16px",
                          padding: "24px",
                          maxWidth: "400px",
                          width: "90%",
                          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={classes.datePickerHeader}>
                          <Typography variant="subtitle2">
                            Select Date Range
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowDatePicker(false)}
                            className={classes.closeDatePicker}
                          >
                            ✕
                          </IconButton>
                        </div>
                        <div className={classes.datePickerContent}>
                          <Typography
                            variant="caption"
                            className={classes.datePickerLabel}
                          >
                            Quick Select:
                          </Typography>
                          <div className={classes.quickDateButtons}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleQuickDateRange(1)}
                              className={classes.quickDateButton}
                            >
                              Today
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleQuickDateRange(7)}
                              className={classes.quickDateButton}
                            >
                              Next 7 Days
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleQuickDateRange(14)}
                              className={classes.quickDateButton}
                            >
                              Next 14 Days
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleQuickDateRange(30)}
                              className={classes.quickDateButton}
                            >
                              Next 30 Days
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleQuickDateRange(60)}
                              className={classes.quickDateButton}
                            >
                              Next 60 Days
                            </Button>
                          </div>
                          <div className={classes.customDateInputs}>
                            <Typography
                              variant="caption"
                              className={classes.datePickerLabel}
                            >
                              Or select custom range:
                            </Typography>
                            <div className={classes.dateInputRow}>
                              <TextField
                                type="date"
                                size="medium"
                                label="Start Date"
                                value={startDate}
                                onChange={(e) => {
                                  const newStartDate = e.target.value;
                                  setStartDate(newStartDate);
                                  // If end date is before new start date, update end date
                                  if (endDate && newStartDate > endDate) {
                                    setEndDate(newStartDate);
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                className={classes.customDateInput}
                                inputProps={{
                                  min: getTodayDateString(),
                                }}
                              />
                              <TextField
                                type="date"
                                size="medium"
                                label="End Date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                className={classes.customDateInput}
                                inputProps={{
                                  min: startDate || getTodayDateString(),
                                }}
                              />
                            </div>
                            <Button
                              variant="contained"
                              size="small"
                              fullWidth
                              className={classes.applyDateButton}
                              onClick={handleApplyCustomRange}
                              disabled={!startDate || !endDate}
                            >
                              Apply Range
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Days Selection */}
            <div className={classes.configSection}>
              <div className={classes.sectionHeader}>
                <Typography variant="caption" className={classes.sectionLabel}>
                  REPEAT ON DAYS
                </Typography>
                <div
                  className={classes.selectAllButton}
                  onClick={handleSelectAll}
                >
                  {selectedDays.every((day) => day)
                    ? "Deselect All"
                    : "Select All"}
                </div>
              </div>
              <div className={classes.daysSelector}>
                {dayLabels.map((day, index) => (
                  <div
                    key={index}
                    className={`${classes.dayLabel} ${
                      selectedDays[index] ? classes.checked : ""
                    }`}
                    onClick={() => handleDayToggle(index)}
                  >
                    <Checkbox
                      checked={selectedDays[index]}
                      onChange={() => handleDayToggle(index)}
                      className={classes.dayCheckbox}
                    />
                    <span className={classes.dayLabelText}>{day}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Time Ranges */}
            <div className={classes.configSection}>
              <Typography variant="caption" className={classes.sectionLabel}>
                2. DEFINE TIME RANGES
              </Typography>

              {timeRanges.map((range, index) => (
                <Card key={range.id} className={classes.timeRangeCard}>
                  <CardContent className={classes.timeRangeContent}>
                    <div className={classes.rangeHeader}>
                      <Typography
                        variant="caption"
                        className={classes.rangeLabel}
                      >
                        RANGE #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeTimeRange(range.id)}
                        className={classes.deleteButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>

                    <div className={classes.timeInputs}>
                      <div className={classes.timeInput}>
                        <Typography variant="caption">Start Time</Typography>
                        <TextField
                          type="time"
                          value={range.startTime}
                          onChange={(e) =>
                            updateTimeRange(
                              range.id,
                              "startTime",
                              e.target.value
                            )
                          }
                          size="small"
                          fullWidth
                          inputProps={{
                            min: "00:00",
                            max: "23:59",
                            step: "300", // 5 minute intervals
                          }}
                        />
                      </div>
                      <div className={classes.timeInput}>
                        <Typography variant="caption">End Time</Typography>
                        <TextField
                          type="time"
                          value={range.endTime}
                          onChange={(e) =>
                            updateTimeRange(range.id, "endTime", e.target.value)
                          }
                          size="small"
                          fullWidth
                          inputProps={{
                            min: "00:00",
                            max: "23:59",
                            step: "300", // 5 minute intervals
                          }}
                        />
                      </div>
                    </div>

                    <RadioGroup
                      value={range.type}
                      onChange={(e) =>
                        updateTimeRange(range.id, "type", e.target.value)
                      }
                      className={classes.typeSelector}
                    >
                      <FormControlLabel
                        value="free"
                        control={<Radio size="small" />}
                        label="FREE (15m)"
                        className={classes.typeOption}
                      />
                      <FormControlLabel
                        value="paid"
                        control={<Radio size="small" />}
                        label="PAID (30m)"
                        className={classes.typeOption}
                      />
                    </RadioGroup>
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={addTimeRange}
                className={classes.addRangeButton}
              >
                Add Time Range
              </Button>
            </div>

            {/* Action Buttons */}
            <div className={classes.actionButtons}>
              {!hasValidTimeRanges && (
                <div className={classes.timeValidationError}>
                  <Typography variant="caption" className={classes.errorText}>
                    ⚠️ Please add at least one time range with start and end
                    times.
                  </Typography>
                </div>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                className={classes.generateButton}
                onClick={handleGenerateSlots}
                disabled={isGenerating || !hasValidTimeRanges}
              >
                {isGenerating ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginRight: 8 }}
                    />
                    Generating...
                  </>
                ) : (
                  "Generate Slots"
                )}
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Schedule View */}
        <main className={classes.scheduleMain}>
          <div className={classes.scheduleHeader}>
            <div className={classes.scheduleTitle}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Typography variant="h6">
                  {doctorName
                    ? `${doctorName}'s Schedule`
                    : "Doctor's Schedule"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => fetchSlotsForWeek(currentWeekStart, true)}
                  title="Refresh data"
                  disabled={isLoadingSlots}
                  style={{ color: "#64748b" }}
                >
                  {isLoadingSlots ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                {bulkDeleteMode && selectedSlots.size > 0 && (
                  <Typography
                    variant="caption"
                    className={classes.selectedCount}
                  >
                    {selectedSlots.size} slot(s) selected
                  </Typography>
                )}

                {/* Bulk Delete Controls */}
                {bulkDeleteMode && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleToggleBulkDelete}
                      startIcon={<DeleteIcon />}
                      style={{
                        color: "#ef4444",
                        borderColor: "#ef4444",
                        fontSize: "12px",
                        padding: "4px 12px",
                      }}
                    >
                      Exit Bulk
                    </Button>
                    {selectedSlots.size > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleDeleteSelectedSlots}
                        disabled={isDeleting}
                        startIcon={
                          isDeleting ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteIcon />
                          )
                        }
                        style={{
                          backgroundColor: "#dc2626",
                          color: "white",
                          fontSize: "12px",
                          padding: "4px 12px",
                        }}
                      >
                        {isDeleting
                          ? "Deleting..."
                          : `Delete ${selectedSlots.size}`}
                      </Button>
                    )}
                  </div>
                )}

                {/* Reschedule Controls */}
                {rescheduleMode && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancelReschedule}
                      startIcon={<DeleteIcon />}
                      style={{
                        color: "#ef4444",
                        borderColor: "#ef4444",
                        fontSize: "12px",
                        padding: "4px 12px",
                      }}
                    >
                      Cancel
                    </Button>
                    {selectedRescheduleSlot && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleConfirmReschedule}
                        disabled={isRescheduling}
                        startIcon={
                          isRescheduling ? (
                            <CircularProgress size={14} />
                          ) : (
                            <RescheduleIcon />
                          )
                        }
                        style={{
                          backgroundColor: "#1976d2",
                          color: "white",
                          fontSize: "12px",
                          padding: "4px 12px",
                        }}
                      >
                        {isRescheduling
                          ? "Rescheduling..."
                          : "Confirm Reschedule"}
                      </Button>
                    )}
                  </div>
                )}

                {/* Reschedule Mode Info */}
                {rescheduleMode && sessionType === "paid" && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Chip
                      label="Showing PAID slots only"
                      size="small"
                      sx={{
                        backgroundColor: "#cce5ff",
                        color: "#004085",
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                  </div>
                )}
                {rescheduleMode && sessionType === "free" && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Chip
                      label="Showing FREE slots only"
                      size="small"
                      sx={{
                        backgroundColor: "#d4edda",
                        color: "#155724",
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                    {isDoctorChanged && (
                      <Chip
                        label="Doctor changed"
                        size="small"
                        sx={{
                          backgroundColor: "#fff3cd",
                          color: "#856404",
                          fontWeight: 500,
                          fontSize: "11px",
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={classes.weekNavigation}>
              <IconButton
                size="small"
                onClick={() => navigateWeek(-1)}
                aria-label="Previous week"
              >
                <ChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                className={classes.weekLabel}
                onClick={handleWeekPickerClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleWeekPickerClick();
                  }
                }}
                aria-label="Select week"
              >
                {currentWeek}
              </Typography>
              <IconButton
                size="small"
                onClick={() => navigateWeek(1)}
                aria-label="Next week"
              >
                <ChevronRight />
              </IconButton>

              {/* Slot Type Filter */}
              {!rescheduleMode && (
                <FormControl size="small" style={{ minWidth: 120, marginLeft: "16px" }}>
                  <InputLabel id="slot-type-filter-label">Filter</InputLabel>
                  <Select
                    labelId="slot-type-filter-label"
                    value={slotTypeFilter}
                    label="Filter"
                    onChange={(e) => setSlotTypeFilter(e.target.value)}
                    style={{ fontSize: "14px" }}
                  >
                    <MenuItem value="all">All Slots</MenuItem>
                    <MenuItem value="free">FREE Only</MenuItem>
                    <MenuItem value="paid">PAID Only</MenuItem>
                  </Select>
                </FormControl>
              )}

              {showWeekPicker && (
                <div className={classes.weekPickerOverlay}>
                  <div className={classes.weekPickerContainer}>
                    <div className={classes.weekPickerHeader}>
                      <Typography variant="subtitle2">
                        Select Week Range
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setShowWeekPicker(false)}
                        className={classes.closeWeekPicker}
                      >
                        ✕
                      </IconButton>
                    </div>

                    <div className={classes.selectionInstructions}>
                      <div className={classes.instructionRow}>
                        <Typography
                          variant="caption"
                          className={classes.instructionText}
                        >
                          {!selectedStartDate
                            ? "Click on a date to select the start of your week"
                            : !selectedEndDate
                            ? "Click on another date to complete your week selection (must be exactly 7 days apart)"
                            : "Week selected successfully!"}
                        </Typography>
                        {selectedStartDate && (
                          // <Button
                          //   size="small"
                          //   onClick={clearSelection}
                          //   className={classes.clearButton}
                          // >
                          //   Clear Selection
                          // </Button>
                          <div
                            onClick={clearSelection}
                            className={classes.clearButton}
                          >
                            Clear Selection
                          </div>
                        )}
                      </div>
                    </div>

                    {calendarError && (
                      <div className={classes.errorMessage}>
                        <Typography
                          variant="caption"
                          className={classes.errorText}
                        >
                          {calendarError}
                        </Typography>
                      </div>
                    )}
                    <div className={classes.calendarContainer}>
                      <div className={classes.calendarHeader}>
                        <IconButton
                          size="small"
                          onClick={() => navigateCalendarMonth(-1)}
                          className={classes.monthNavButton}
                        >
                          <ChevronLeft />
                        </IconButton>
                        <Typography variant="h6">
                          {calendarMonth.toLocaleDateString("en-US", {
                            month: "long",
                            year: "numeric",
                          })}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => navigateCalendarMonth(1)}
                          className={classes.monthNavButton}
                        >
                          <ChevronRight />
                        </IconButton>
                      </div>
                      <div className={classes.calendarGrid}>
                        <div className={classes.dayHeaders}>
                          {[
                            "Sun",
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                          ].map((day) => (
                            <div key={day} className={classes.dayHeader}>
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className={classes.daysGrid}>
                          {generateCalendarDays().map((date, index) => {
                            const isCurrentMonth =
                              date.getMonth() === calendarMonth.getMonth();
                            const isInCurrentWeek = isDateInCurrentWeek(date);
                            const isToday =
                              date.toDateString() === new Date().toDateString();
                            const isSelected = isDateSelected(date);
                            const isInSelectionRange = isDateInRange(date);

                            return (
                              <div
                                key={index}
                                className={`${classes.calendarDay} ${
                                  !isCurrentMonth ? classes.otherMonth : ""
                                } ${
                                  isInCurrentWeek ? classes.selectedWeek : ""
                                } ${isToday ? classes.today : ""} ${
                                  isSelected ? classes.selectedDate : ""
                                } ${isInSelectionRange ? classes.inRange : ""}`}
                                onClick={() => handleDateSelect(date)}
                              >
                                {date.getDate()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={classes.scheduleContent}>
            {isLoadingSlots ? (
              <div className={classes.loadingSlotsContainer}>
                <CircularProgress size={40} />
                <Typography
                  variant="body2"
                  className={classes.loadingSlotsMessage}
                >
                  Loading schedule…
                </Typography>
              </div>
            ) : (
              <>
                {/* Week Header */}
                <div className={classes.weekHeader}>
                  {getWeekHeaderData().map((header) => (
                    <div
                      key={header.key}
                      className={`${classes.dayHeader} ${
                        header.isToday ? classes.todayHeader : ""
                      }`}
                    >
                      <Typography variant="caption" className={classes.dayName}>
                        {header.dayName}
                      </Typography>
                      <Typography variant="h6" className={classes.dayDate}>
                        {header.date}
                      </Typography>
                      {header.isToday && (
                        <div className={classes.todayIndicator}></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Schedule Grid */}
                <div className={classes.scheduleGrid}>
                  {getWeekHeaderData().map((header) => {
                    const slots = weekSlots[header.key] || [];

                    // Group slots by time for spacing
                    const groupedSlots = [];
                    const timeGroups = {};

                    // Group slots by time, filtering for reschedule mode and type filter
                    slots.forEach((slot) => {
                      // Filter slots based on reschedule mode, session type, and type filter
                      if (!shouldShowSlot(slot)) {
                        return; // Skip this slot
                      }

                      const timeKey = slot.time;
                      if (!timeGroups[timeKey]) {
                        timeGroups[timeKey] = [];
                      }
                      timeGroups[timeKey].push(slot);
                    });

                    // Convert to array with spacers
                    const timeKeys = Object.keys(timeGroups).sort();
                    timeKeys.forEach((timeKey, timeIndex) => {
                      // Add all slots for this time
                      timeGroups[timeKey].forEach((slot, slotIndex) => {
                        groupedSlots.push({
                          type: "slot",
                          slot: slot,
                          key: `${slot.slotId || `${timeKey}-${slotIndex}`}`,
                        });
                      });

                      // Add spacer after each time group (except the last one)
                      if (timeIndex < timeKeys.length - 1) {
                        groupedSlots.push({
                          type: "spacer",
                          key: `spacer-${timeKey}`,
                        });
                      }
                    });

                    return (
                      <div key={header.key} className={classes.dayColumn}>
                        {groupedSlots.length === 0 ? (
                          <div className={classes.dayColumnEmpty}>
                            <Typography variant="caption">No slots</Typography>
                          </div>
                        ) : (
                          groupedSlots.map((item) => {
                            if (item.type === "spacer") {
                              return (
                                <div
                                  key={item.key}
                                  className={classes.timeGroupSpacer}
                                />
                              );
                            }

                            const slot = item.slot;
                            return (
                              <Card
                                key={item.key}
                                className={`${classes.slotCard} ${
                                  classes[slot.status]
                                } ${
                                  slot.status === "available"
                                    ? classes[slot.type]
                                    : ""
                                } ${
                                  bulkDeleteMode &&
                                  selectedSlots.has(slot.slotId)
                                    ? classes.selectedSlot
                                    : ""
                                } ${
                                  rescheduleMode &&
                                  selectedRescheduleSlot === slot.slotId
                                    ? classes.selectedSlot
                                    : ""
                                } ${
                                  bulkDeleteMode && slot.status === "available"
                                    ? classes.selectableCard
                                    : ""
                                } ${
                                  rescheduleMode && slot.status === "available"
                                    ? classes.selectableCard
                                    : ""
                                }`}
                                onClick={
                                  bulkDeleteMode && slot.status === "available"
                                    ? () =>
                                        handleSlotSelect(
                                          slot.slotId,
                                          !selectedSlots.has(slot.slotId)
                                        )
                                    : rescheduleMode &&
                                      slot.status === "available"
                                    ? () => handleRescheduleSlotSelect(slot)
                                    : undefined
                                }
                              >
                                <CardContent className={classes.slotContent}>
                                  {bulkDeleteMode &&
                                    slot.status === "available" && (
                                      <div className={classes.slotCheckbox}>
                                        <Checkbox
                                          size="small"
                                          checked={selectedSlots.has(
                                            slot.slotId
                                          )}
                                          onChange={() => {}} // Disabled since card click handles selection
                                          className={classes.slotSelectCheckbox}
                                          onClick={(e) => e.stopPropagation()} // Prevent double-click
                                        />
                                      </div>
                                    )}
                                  {rescheduleMode &&
                                    slot.status === "available" && (
                                      <div className={classes.slotCheckbox}>
                                        <Checkbox
                                          size="small"
                                          checked={
                                            selectedRescheduleSlot ===
                                            slot.slotId
                                          }
                                          onChange={() => {}} // Disabled since card click handles selection
                                          className={classes.slotSelectCheckbox}
                                          onClick={(e) => e.stopPropagation()} // Prevent double-click
                                        />
                                      </div>
                                    )}
                                  <Typography
                                    variant="body2"
                                    className={classes.slotTime}
                                  >
                                    {slot.time}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    className={classes.slotDuration}
                                  >
                                    {slot.duration
                                      ? `${Math.round(slot.duration / 60)} min`
                                      : ""}
                                  </Typography>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginTop: "4px",
                                    }}
                                  >
                                    <Chip
                                      label={
                                        slot.type === "free" ? "FREE" : "PAID"
                                      }
                                      size="small"
                                      style={{
                                        height: "18px",
                                        fontSize: "9px",
                                        fontWeight: "bold",
                                        backgroundColor:
                                          slot.type === "free"
                                            ? "#10b981"
                                            : "#138aec",
                                        color: "white",
                                      }}
                                    />
                                    {slot.status === "booked" ? (
                                      <div className={classes.bookedIndicator}>
                                        <span>🔒</span>
                                        <Typography variant="caption">
                                          Booked
                                        </Typography>
                                      </div>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        className={classes.availableIndicator}
                                      >
                                        Available
                                      </Typography>
                                    )}
                                  </div>
                                  {!bulkDeleteMode &&
                                    !rescheduleMode &&
                                    slot.status === "available" && (
                                      <div className={classes.slotActions}>
                                        <IconButton
                                          size="small"
                                          className={classes.slotDeleteButton}
                                          title="Delete"
                                          aria-label="Delete slot"
                                          onClick={() =>
                                            handleOpenDeleteModal(slot)
                                          }
                                          disabled={isDeleting}
                                        >
                                          {isDeleting ? (
                                            <CircularProgress
                                              size={16}
                                              color="inherit"
                                            />
                                          ) : (
                                            <DeleteIcon />
                                          )}
                                        </IconButton>
                                      </div>
                                    )}
                                </CardContent>
                              </Card>
                            );
                          })
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </main>

        {/* Delete Options Modal */}
        {showDeleteModal && selectedSlotForDelete && (
          <div className={classes.modalOverlay}>
            <div className={classes.modalContainer}>
              <div className={classes.modalHeader}>
                <Typography variant="h6" className={classes.modalTitle}>
                  Delete Slot Options
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowDeleteModal(false)}
                  className={classes.closeModalButton}
                >
                  ✕
                </IconButton>
              </div>

              <div className={classes.modalContent}>
                <div className={classes.deleteOptions}>
                  <div
                    className={classes.deleteOption}
                    onClick={() => setDeleteOption("single")}
                    style={{ cursor: "pointer" }}
                  >
                    <Radio
                      checked={deleteOption === "single"}
                      onChange={() => setDeleteOption("single")}
                      value="single"
                      size="small"
                    />
                    <Typography variant="body2">
                      Delete only this {selectedSlotForDelete.time} slot (
                      {formatDateForDisplay(new Date())})
                    </Typography>
                  </div>

                  <div
                    className={classes.deleteOption}
                    onClick={() => setDeleteOption("bulk")}
                    style={{ cursor: "pointer" }}
                  >
                    <Radio
                      checked={deleteOption === "bulk"}
                      onChange={() => setDeleteOption("bulk")}
                      value="bulk"
                      size="small"
                    />
                    <Typography variant="body2">
                      Select multiple slots to delete
                    </Typography>
                  </div>

                  <div
                    className={classes.deleteOption}
                    onClick={() => setDeleteOption("range")}
                    style={{ cursor: "pointer" }}
                  >
                    <Radio
                      checked={deleteOption === "range"}
                      onChange={() => setDeleteOption("range")}
                      value="range"
                      size="small"
                    />
                    <div className={classes.rangeOption}>
                      <Typography variant="body2">
                        Delete all {selectedSlotForDelete.time} slots in date
                        range:
                      </Typography>
                      <div
                        className={classes.dateRangeInputs}
                        onClick={(e) => e.stopPropagation()} // Prevent option selection when clicking on date inputs
                      >
                        <TextField
                          type="date"
                          size="small"
                          label="Start Date"
                          value={deleteRangeStart}
                          onChange={(e) => setDeleteRangeStart(e.target.value)}
                          onFocus={() => setDeleteOption("range")}
                          InputLabelProps={{ shrink: true }}
                          className={classes.dateRangeInput}
                          inputProps={{
                            min: getTodayDateString(),
                          }}
                        />
                        <TextField
                          type="date"
                          size="small"
                          label="End Date"
                          value={deleteRangeEnd}
                          onChange={(e) => setDeleteRangeEnd(e.target.value)}
                          onFocus={() => setDeleteOption("range")}
                          InputLabelProps={{ shrink: true }}
                          className={classes.dateRangeInput}
                          inputProps={{
                            min: deleteRangeStart || getTodayDateString(),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {deleteOption === "range" &&
                  deleteRangeStart &&
                  deleteRangeEnd && (
                    <div className={classes.previewText}>
                      <Typography
                        variant="caption"
                        className={classes.previewMessage}
                      >
                        This will delete all {selectedSlotForDelete.time} slots
                        (
                        <span
                          className={`${classes.slotTypeHighlight} ${classes.freeType}`}
                        >
                          FREE
                        </span>
                        {" & "}
                        <span
                          className={`${classes.slotTypeHighlight} ${classes.paidType}`}
                        >
                          PAID
                        </span>
                        ) from {deleteRangeStart} to {deleteRangeEnd}
                      </Typography>
                    </div>
                  )}
              </div>

              <div className={classes.modalActions}>
                <Button
                  variant="outlined"
                  onClick={() => setShowDeleteModal(false)}
                  className={classes.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDeleteModalProceed}
                  className={classes.proceedButton}
                  disabled={
                    isDeleting ||
                    (deleteOption === "range" &&
                      (!deleteRangeStart || !deleteRangeEnd))
                  }
                >
                  {isDeleting ? (
                    <>
                      <CircularProgress
                        size={16}
                        color="inherit"
                        style={{ marginRight: 8 }}
                      />
                      Processing...
                    </>
                  ) : (
                    "Proceed"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhysioSlots;
